import { useEffect, useState } from "react";
import { useApiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, Plus, Receipt, CheckCircle2 } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";

export function CompleteModal({ appt, clients, services, products, packages, onClose, onSaved }) {
  const api = useApiClient();
  const svcIds = appt.service_ids?.length ? appt.service_ids : (appt.service_id ? [appt.service_id] : []);
  const initialLines = svcIds
    .map((sid) => services.find((s) => s.id === sid))
    .filter(Boolean)
    .map((s) => ({ kind: "service", description: s.name, quantity: 1, unit_price: s.price, service_id: s.id }));

  const [lines, setLines] = useState(initialLines.length ? initialLines : [{ kind: "service", description: "", quantity: 1, unit_price: 0 }]);
  const [payment, setPayment] = useState("cash");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const client = clients.find((c) => c.id === appt.client_id);

  // Global Esc closes the modal — consistency with ApptModal / DayOverviewModal
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Client's active packages — used to hint auto-scale on service line matches
  const [clientPackages, setClientPackages] = useState([]);
  useEffect(() => {
    if (!appt.client_id) return;
    let alive = true;
    api.get(`/clients/${appt.client_id}/packages`).then((r) => {
      if (alive) setClientPackages(r.data || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [appt.client_id]);
  const packageSlotFor = (serviceId) => {
    if (!serviceId) return null;
    for (const cp of clientPackages) {
      const slot = (cp.slots || []).find((s) => !s.redeemed && s.service_id === serviceId);
      if (slot) return { cp, slot };
    }
    return null;
  };

  const total = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const addLine = (kind = "product") => setLines([...lines, { kind, description: "", quantity: 1, unit_price: 0 }]);

  // If a product row's product_id changes → autofill desc + price
  const onProductPick = (i, pid) => {
    const prd = (products || []).find((p) => p.id === pid);
    if (!prd) { updateLine(i, { product_id: null }); return; }
    updateLine(i, { product_id: prd.id, description: prd.name, unit_price: (prd.retail_price ?? prd.price ?? prd.unit_cost ?? 0) });
  };

  // If a package row's package_id changes → autofill desc + promo_price
  const onPackagePick = (i, pid) => {
    const pkg = (packages || []).find((p) => p.id === pid);
    if (!pkg) { updateLine(i, { package_id: null }); return; }
    updateLine(i, { package_id: pkg.id, description: pkg.name, unit_price: (pkg.promo_price ?? 0) });
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleanLines = lines.filter((l) => (Number(l.quantity) || 0) > 0 && (Number(l.unit_price) || 0) >= 0 && (l.description || l.service_id || l.product_id || l.package_id));
    if (cleanLines.length === 0) { toast.error("Aggiungi almeno una riga"); return; }
    // Warn if a product line would put stock below zero
    for (const l of cleanLines) {
      if (l.kind === "product" && l.product_id) {
        const prd = (products || []).find((p) => p.id === l.product_id);
        if (prd && (prd.quantity ?? 0) < Number(l.quantity)) {
          if (!window.confirm(`Attenzione: hai solo ${prd.quantity ?? 0} pezzi di "${prd.name}" a magazzino. Vendere lo stesso ${l.quantity}?`)) return;
        }
      }
    }
    setSaving(true);
    try {
      await api.post("/invoices", {
        client_id: appt.client_id,
        appointment_id: appt.id,
        lines: cleanLines.map((l) => ({
          description: l.description || null,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          service_id: l.service_id || null,
          product_id: l.product_id || null,
          package_id: l.package_id || null,
          kind: l.kind || "other",
        })),
        payment_method: payment,
        tax_rate: 0,
        notes: notes || null,
      });
      // Also persist the note as a manual activity on the client timeline so
      // it appears in Anagrafica + Pipeline note popover (single source of truth).
      if (notes && notes.trim()) {
        try {
          await api.post(`/clients/${appt.client_id}/activities/manual`, {
            text: `[Chiusura appuntamento] ${notes.trim()}`,
          });
        } catch { /* best-effort — don't block the invoice save */ }
      }
      toast.success(`Appuntamento completato · Incassato € ${total.toFixed(2)}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="sp-card w-full max-w-5xl p-5 sm:p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-2xl font-black inline-flex items-center gap-2"><Receipt className="h-5 w-5 text-[color:var(--sp-success)]" /> Chiusura appuntamento</h3>
            <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1">
              {client?.name || "Cliente"} · {new Date(appt.start).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Lines table */}
          <div className="rounded-lg border border-[color:var(--sp-border)] overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-[color:var(--sp-subtle)] label-eyebrow text-[10px]">
              <div className="col-span-2">Tipo</div>
              <div className="col-span-5">Descrizione / Prodotto</div>
              <div className="col-span-1 text-right">Qt.</div>
              <div className="col-span-2 text-right">Prezzo</div>
              <div className="col-span-1 text-right">Totale</div>
              <div className="col-span-1"></div>
            </div>
            {lines.map((l, i) => {
              const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
              const isProduct = l.kind === "product";
              const prd = isProduct && l.product_id ? (products || []).find((p) => p.id === l.product_id) : null;
              return (
                <div key={i} data-testid={`complete-line-${i}`} className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-3 py-3 border-t border-[color:var(--sp-border)] items-center">
                  <div className="col-span-2 sm:col-span-2">
                    <select value={l.kind || "service"} onChange={(e) => updateLine(i, { kind: e.target.value, product_id: null, service_id: null, package_id: null })} className="sp-input !py-1.5 text-sm w-full">
                      <option value="service">Servizio</option>
                      <option value="product">Prodotto</option>
                      <option value="package">Percorso</option>
                      <option value="other">Altro</option>
                    </select>
                  </div>
                  <div className="col-span-2 sm:col-span-5">
                    {isProduct && (products || []).length > 0 ? (
                      <SearchableSelect
                        testId={`complete-line-product-${i}`}
                        size="md"
                        value={l.product_id || ""}
                        onChange={(v) => onProductPick(i, v)}
                        options={(products || []).map((p) => ({
                          id: p.id,
                          label: p.name,
                          sub: `stock: ${p.quantity ?? 0}${p.min_stock && (p.quantity ?? 0) <= p.min_stock ? " ⚠" : ""}`,
                        }))}
                        placeholder="— Scegli dal magazzino —"
                      />
                    ) : l.kind === "package" && (packages || []).length > 0 ? (
                      <SearchableSelect
                        testId={`complete-line-package-${i}`}
                        size="md"
                        value={l.package_id || ""}
                        onChange={(v) => onPackagePick(i, v)}
                        options={(packages || []).map((p) => ({
                          id: p.id,
                          label: p.name,
                          sub: `€ ${(p.promo_price ?? 0).toFixed ? p.promo_price.toFixed(2) : p.promo_price}`,
                          color: p.color,
                        }))}
                        placeholder="— Scegli un percorso —"
                      />
                    ) : l.kind === "service" && services.length > 0 && !l.description ? (
                      <SearchableSelect
                        testId={`complete-line-service-${i}`}
                        size="md"
                        value={l.service_id || ""}
                        onChange={(v) => {
                          const svc = services.find((s) => s.id === v);
                          if (svc) updateLine(i, { service_id: svc.id, description: svc.name, unit_price: svc.price });
                        }}
                        options={services.map((s) => ({ id: s.id, label: s.name, sub: `€ ${s.price}` }))}
                        placeholder="— Scegli dal catalogo —"
                      />
                    ) : (
                      <input data-testid={`complete-line-desc-${i}`} className="sp-input !py-1.5 text-sm w-full" placeholder={l.kind === "product" ? "Nome prodotto" : "Descrizione"} value={l.description || ""} onChange={(e) => updateLine(i, { description: e.target.value })} />
                    )}
                    {prd && (
                      <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">
                        Stock attuale: <strong>{prd.quantity ?? 0}</strong> — dopo la vendita: <strong className={((prd.quantity ?? 0) - Number(l.quantity)) < 0 ? "text-[color:var(--sp-danger)]" : ""}>{(prd.quantity ?? 0) - Number(l.quantity)}</strong>
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 sm:col-span-1">
                    <label className="sm:hidden label-eyebrow text-[9px] block mb-0.5">Qt.</label>
                    <input
                      data-testid={`complete-line-qty-${i}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="sp-input !py-1.5 text-sm text-right no-spinners w-full"
                      value={l.quantity}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => updateLine(i, { quantity: e.target.value.replace(/[^0-9]/g, "") })}
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <label className="sm:hidden label-eyebrow text-[9px] block mb-0.5">Prezzo €</label>
                    <input
                      data-testid={`complete-line-price-${i}`}
                      type="text"
                      inputMode="decimal"
                      className="sp-input !py-1.5 text-sm text-right no-spinners w-full"
                      value={l.unit_price}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => updateLine(i, { unit_price: e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 text-right font-bold whitespace-nowrap tabular-nums text-sm sm:text-base">€ {lineTotal.toFixed(2)}</div>
                  <div className="col-span-2 sm:col-span-1 text-right">
                    <button type="button" onClick={() => removeLine(i)} className="p-1.5 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]" title="Rimuovi riga"><X className="h-4 w-4" /></button>
                  </div>
                  {/* Package auto-redeem hint — shown when this service line
                      matches an unredeemed slot in a client's active package */}
                  {l.kind === "service" && l.service_id && (() => {
                    const match = packageSlotFor(l.service_id);
                    if (!match) return null;
                    const cp = match.cp;
                    const remaining = (cp.slots || []).filter((s) => !s.redeemed).length;
                    return (
                      <div className="col-span-12 -mt-1 mb-1 mx-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px]" data-testid={`complete-line-package-match-${i}`}>
                        <div className="font-black text-emerald-800 inline-flex items-center gap-1">
                          ✨ Scalato automaticamente dal percorso <span className="underline">{cp.package_name}</span> ({remaining - 1} su {cp.slots.length} sedute rimanenti)
                        </div>
                      </div>
                    );
                  })()}
                  {/* Package bonus preview — reminds the salon what to give the client */}
                  {l.kind === "package" && l.package_id && (() => {
                    const pkg = (packages || []).find((p) => p.id === l.package_id);
                    const bonuses = pkg?.bonus_items || [];
                    if (!bonuses.length) return null;
                    return (
                      <div key={`bonus-${i}`} className="col-span-12 -mt-1 mb-1 mx-2 px-3 py-2 rounded-lg bg-[color:var(--sp-brand)]/8 border border-[color:var(--sp-brand)]/25 text-[11px]" data-testid={`complete-line-package-bonuses-${i}`}>
                        <div className="font-black text-[color:var(--sp-brand)] inline-flex items-center gap-1 mb-1">
                          🎁 In omaggio da consegnare al cliente:
                        </div>
                        <ul className="space-y-0.5 pl-4">
                          {bonuses.map((b, bi) => (
                            <li key={bi} className="text-[color:var(--sp-text-secondary)] list-disc">
                              <span className="font-bold">{b.name}</span>
                              {b.quantity > 1 && <span className="ml-1 text-[10px] font-black">×{b.quantity}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {/* Add row */}
          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" data-testid="complete-add-service" onClick={() => addLine("service")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Servizio extra</button>
            <button type="button" data-testid="complete-add-product" onClick={() => addLine("product")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Prodotto venduto</button>
            <button type="button" data-testid="complete-add-package" onClick={() => addLine("package")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Percorso venduto</button>
            <button type="button" data-testid="complete-add-other" onClick={() => addLine("other")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Voce libera</button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold">Metodo di pagamento</label>
              <select data-testid="complete-payment" className="sp-input mt-1" value={payment} onChange={(e) => setPayment(e.target.value)}>
                <option value="cash">Contanti</option>
                <option value="card">Carta</option>
                <option value="bank_transfer">Bonifico</option>
                <option value="other">Altro</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-bold">Note (opzionale)</label>
              <input data-testid="complete-notes" className="sp-input mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-[color:var(--sp-brand)]/10 border border-[color:var(--sp-brand)]/30">
            <div>
              <span className="text-sm font-bold text-[color:var(--sp-brand)] uppercase tracking-widest">Totale incasso</span>
              {lines.some((l) => l.kind === "product" && l.product_id) && (
                <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">Lo stock del magazzino verrà aggiornato automaticamente.</div>
              )}
            </div>
            <span data-testid="complete-total" className="font-display text-3xl font-black text-[color:var(--sp-brand)]">€ {total.toFixed(2)}</span>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="sp-btn-ghost !py-2 !px-4 text-sm flex-1">Annulla</button>
            <button data-testid="complete-submit" disabled={saving} className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2 rounded-full text-sm font-bold bg-[color:var(--sp-success)] text-white hover:opacity-90 disabled:opacity-50">
              <CheckCircle2 className="h-4 w-4" /> {saving ? "Registro…" : "Conferma e chiudi"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
